import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import type { BoxProps } from '../box/Box';
import type { GenericUIProps, NameSizingScale, TransformedCSSProperties } from '../component.types';
import Image from '../image/Image';
import { transformSpecialCSSProperties } from '../utils';
import styles from './List.module.scss';

/**
 * Lists & Stuff
 * Borrows heavily from DropdownMenu/Items.
 * @todo Basic keyboard accessibility?
 */

export interface CommonProps {
  /**
   * Render the component _onto the child_
   * @link https://www.radix-ui.com/primitives/docs/utilities/slot
   */
  asChild?: boolean;
  /** Whether the item appears as interacted with (hover effect) */
  active?: boolean;
  /** Make non-interactive */
  disabled?: boolean;
  /** Control event handling (useful for buttons inside links, etc) */
  preventDefault?: boolean;
  /** Control event progatation (useful for buttons inside links, etc) */
  stopPropagation?: boolean;
  /** Control (flex) direction of list */
  size?: 'xsmall' | NameSizingScale | 'auto';
}

export interface UlProps
  extends React.HTMLAttributes<HTMLUListElement>,
    TransformedCSSProperties,
    Pick<CommonProps, 'asChild'> {
  /** Sets `flex-direction` of List container */
  orientation?: 'horizontal' | 'vertical';
}

/**
 * Aka <ul />.
 */
const Container = React.forwardRef<HTMLUListElement, UlProps>((props, ref) => {
  const {
    asChild,
    className,
    orientation = 'vertical',
    ...finalProps
  } = transformSpecialCSSProperties(props);
  const Component = asChild ? Slot : 'ul';
  return (
    <Component
      {...finalProps}
      className={classNames(styles.listContainer, styles[`orientation-${orientation}`], className)}
      ref={ref}
    />
  );
});
Container.displayName = 'List.Container';

export interface LiProps
  extends React.HTMLAttributes<HTMLLIElement>,
    CommonProps,
    TransformedCSSProperties {
  interactive?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /** Indentation level (should we do this at List-level?) */
  indentation?: number;
  /** Tie to Form element ID and render as <label>? */
  htmlFor?: string;
  /**
   * Fallback element to render, if no `asChild` or `htmlFor` is supplied.
   * Useful for rendering lists within lists.
   */
  as?: string;
}

/**
 * Aka <li />.
 */
const Item = React.forwardRef<HTMLLIElement | HTMLElement, LiProps>((props, ref) => {
  const {
    asChild,
    className,
    interactive = true,
    size = 'small',
    active,
    onClick,
    preventDefault,
    stopPropagation,
    style,
    htmlFor,
    as,
    indentation = 0,
    ...finalProps
  } = transformSpecialCSSProperties(props);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (preventDefault) e.preventDefault();
      if (stopPropagation) e.stopPropagation();
      onClick?.(e);
    },
    [onClick, preventDefault, stopPropagation]
  );

  const finalStyle = {
    ...style,
    marginLeft: props.indentation ? indentation * 16 : style?.marginLeft,
  };
  const fallbackElement = as || 'li';
  const Component = asChild ? Slot : htmlFor ? 'label' : fallbackElement;
  return (
    <Component
      {...finalProps}
      style={finalStyle}
      ref={ref}
      onClick={handleClick}
      className={classNames(
        styles.listItem,
        styles[`size-${size}`],
        {
          // States
          [styles.interactive]: interactive && !props.disabled,
          [styles.active]: active && !props.disabled,
          [styles.disabled]: props.disabled,
        },
        className
      )}
    />
  );
});

Item.displayName = 'List.Item';

/**
 * Use `src` for an image, or children for a SVG icon.
 * Leave empty to just fill the icon space.
 */
const ListItemIcon = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{ src?: string; className?: string }>
>((props, forwardedRef) => {
  return (
    <div {...props} className={classNames(styles.listItemIcon, props.className)} ref={forwardedRef}>
      {'src' in props ? <Image src={props.src} /> : 'children' in props && props.children}
    </div>
  );
});
ListItemIcon.displayName = 'List.Item.Icon';

export interface LiInteractiveProps
  extends BoxProps,
    Pick<CommonProps, 'asChild' | 'stopPropagation' | 'preventDefault'>,
    Pick<LiProps, 'onClick' | 'indentation'>,
    TransformedCSSProperties {
  htmlFor?: string;
}

const ListItemInteractive = React.forwardRef<HTMLLIElement, LiInteractiveProps>((props, ref) => {
  return (
    <Item
      {...props}
      className={classNames(styles.listItemInteractive, props.className)}
      ref={ref}
      size="small"
      as="div"
    />
  );
});
ListItemInteractive.displayName = 'List.Item.Interactive';

/**
 * List header.
 * @todo Make sure "+" with CmdPalette/DropdownMenu works here
 */
const Header = React.forwardRef<HTMLLIElement, LiProps>((props, ref) => {
  const {
    asChild,
    interactive = false,
    active,
    stopPropagation,
    preventDefault,
    className,
    onClick,
    size = 'small',
    ...finalProps
  } = transformSpecialCSSProperties(props);

  const Component = asChild ? Slot : 'li';
  return (
    <Component
      {...finalProps}
      onClick={!props.disabled ? onClick : undefined}
      className={classNames(
        styles.listHeader,
        styles[`size-${size}`],
        {
          [styles.interactive]: interactive && !props.disabled,
          [styles.active]: active && !props.disabled,
        },
        className
      )}
      ref={ref}
    />
  );
});

Header.displayName = 'List.Header';

function Separator({ className, ...rest }: Omit<GenericUIProps, 'children'>) {
  return (
    <div {...rest} className={classNames(styles.listSeparator, className)}>
      <div />
    </div>
  );
}
Separator.displayName = 'List.Separator';

export const List = Object.assign(Container, {
  Container,
  Item: Object.assign(Item, {
    Icon: ListItemIcon,
    Interactive: ListItemInteractive,
  }),
  Header,
  Separator,
});
